import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Advice,
    Button,
    CheckboxGroup,
    Input,
    Link,
    Spacer,
    Text,
    useTheme,
} from '@Sage/hoi-poi-ui';
import AbsoluteLoader from 'components/AbsoluteLoader';
import useLoginLiterals from 'utils/literals/useLoginLiterals';
import { ensureRoute } from 'utils/routes';

import { createUser } from 'services/Onboarding';
import SageMobileLogo from 'components/SageMobileLogo';

import './styles.scss';

const SageMobileInvite = memo(() => {
    const theme = useTheme();
    const isValidatingForm = useRef(false);
    const touchedRef = useRef({});

    const [state, setState] = useState({
        error: null,
        initialized: false,
        loading: false,
    });

    const urlParams = useMemo(() => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const { token, email, hideTermsAndPrivacy } = Object.fromEntries(urlSearchParams.entries());
        return {
            email,
            token,
            hideTermsAndPrivacy: hideTermsAndPrivacy === 'true',
        };
    }, []);

    const [formState, setFormState] = useState({
        email: '',
        fullName: '',
        checks: {
            privacyPolicy: false,
            termsAndConditions: false,
        },
    });

    const [errors, setErrors] = useState({});

    const { loading: literalsLoading, getLiteral } = useLoginLiterals();
    const t = getLiteral;

    useEffect(() => {
        if (!urlParams.token || !urlParams.email) {
            ensureRoute('/sage-mobile-error');
            return;
        }
        setFormState((prev) => ({ ...prev, email: urlParams.email }));
        setState((prev) => ({ ...prev, initialized: true }));
    }, [urlParams.email, urlParams.token]);

    const isRequired = useCallback((value) => (!value ? t('helptext_required_field') : false), [t]);

    const validateField = useCallback(
        (field, value) => {
            if (field === 'checks' && !isValidatingForm.current) return;
            if (isValidatingForm.current) {
                touchedRef.current = { ...touchedRef.current, [field]: true };
            }

            let error = null;

            switch (field) {
                case 'fullName':
                    error = isRequired(value);
                    break;
                case 'checks':
                    // Skip validation if terms and privacy are hidden (already signed in another country)
                    if (urlParams.hideTermsAndPrivacy) {
                        error = false;
                        break;
                    }
                    if (!value.privacyPolicy || !value.termsAndConditions) {
                        error = {
                            privacyPolicy: !value.privacyPolicy
                                ? t('helptext_required_field')
                                : false,
                            termsAndConditions: !value.termsAndConditions
                                ? t('helptext_required_field')
                                : false,
                        };
                    } else {
                        error = false;
                    }
                    break;
                default:
                    break;
            }

            setErrors({ ...errors, [field]: error });
            return error;
        },
        [errors, isRequired, t, urlParams.hideTermsAndPrivacy],
    );

    const validateForm = useCallback(() => {
        const errors = Object.entries(formState).reduce((obj, [field, value]) => {
            return { ...obj, [field]: validateField(field, value) };
        }, {});

        setErrors(errors);
        return Object.values(errors).filter(Boolean).length === 0;
    }, [formState, validateField]);

    const validForm = useMemo(() => {
        return Object.values(errors).filter(Boolean).length === 0;
    }, [errors]);

    const onBlur = useCallback((field) => {
        touchedRef.current = { ...touchedRef.current, [field]: true };
    }, []);

    const onChange = useCallback(
        (field) => (value) => {
            setFormState({ ...formState, [field]: value });
            validateField(field, value);
        },
        [formState, validateField],
    );

    const handleLink = useCallback((e, url, target = '_blank') => {
        e.stopPropagation();
        window.open(url, target);
    }, []);

    const handleSend = useCallback(
        (e) => {
            e?.preventDefault?.();

            isValidatingForm.current = true;

            if (!validateForm()) {
                return;
            }

            setState((prev) => ({
                ...prev,
                error: null,
                loading: true,
            }));

            // If terms and privacy are hidden, send true (already signed in another country)
            const privacyPolicyValue = urlParams.hideTermsAndPrivacy
                ? true
                : formState.checks.privacyPolicy;
            const termsAndConditionsValue = urlParams.hideTermsAndPrivacy
                ? true
                : formState.checks.termsAndConditions;

            createUser(
                {
                    name: formState.fullName,
                    privatePolicy: privacyPolicyValue,
                    termsAndConditions: termsAndConditionsValue,
                },
                urlParams.token,
            )
                .then(() => {
                    ensureRoute('/sage-mobile-success');
                })
                .catch((e) => {
                    setState((prev) => ({
                        ...prev,
                        error: t('error_generalerror'),
                        loading: false,
                    }));
                    console.error(e);
                    isValidatingForm.current = false;
                });
        },
        [
            formState.checks.privacyPolicy,
            formState.checks.termsAndConditions,
            formState.fullName,
            t,
            urlParams.hideTermsAndPrivacy,
            urlParams.token,
            validateForm,
        ],
    );

    const getLabelWithLink = useCallback(
        ({ label, link, url, target }) => {
            const labelText = t(label);
            const linkText = t(link);
            const linkUrl = t(url);

            const parts = labelText.split('%@');
            if (parts.length === 1) return labelText;

            return (
                <>
                    {parts[0]}
                    <Link
                        className="sage-mobile-invite__link"
                        onClick={(e) => handleLink(e, linkUrl, target)}
                        variation="primary"
                        bold
                    >
                        {linkText}
                    </Link>
                    {parts[1]}
                </>
            );
        },
        [handleLink, t],
    );

    const getChecksOptions = useMemo(() => {
        return [
            {
                label: (
                    <Text type="body" color={theme.colors.textLight.primary}>
                        {getLabelWithLink({
                            label: 'label_privacy_policy',
                            link: 'label_privacy_policy_link',
                            url: 'label_privacy_policy_url',
                        })}
                    </Text>
                ),
                value: 'privacyPolicy',
            },
            {
                label: (
                    <Text type="body" color={theme.colors.textLight.primary}>
                        {getLabelWithLink({
                            label: 'label_terms_and_conditions',
                            link: 'label_terms_and_conditions_link',
                            url: 'label_terms_and_conditions_url',
                        })}
                    </Text>
                ),
                value: 'termsAndConditions',
            },
        ];
    }, [getLabelWithLink, theme.colors.textLight.primary]);

    const btnActive = useMemo(() => {
        if (urlParams.hideTermsAndPrivacy) {
            return formState.email && formState.fullName;
        }
        return (
            formState.email &&
            formState.fullName &&
            formState.checks.privacyPolicy &&
            formState.checks.termsAndConditions
        );
    }, [
        formState.checks.privacyPolicy,
        formState.checks.termsAndConditions,
        formState.email,
        formState.fullName,
        urlParams.hideTermsAndPrivacy,
    ]);

    if (literalsLoading || !state.initialized) {
        return (
            <div className="sage-mobile-invite">
                <AbsoluteLoader size="massive" />
            </div>
        );
    }

    return (
        <div className="sage-mobile-invite">
            <form className="sage-mobile-invite__form" onSubmit={handleSend} autoComplete="off">
                <div className="sage-mobile-invite__logo">
                    <SageMobileLogo />
                </div>
                <Spacer y={3} />
                <Text type="h4" isBold className="sage-mobile-invite__title">
                    {t('label_welcome_sage_copilot')}
                </Text>
                <Spacer y={2} />
                <Text type="body" color={theme.colors.textLight.secondary}>
                    {t('label_accept_invitation_description')}
                </Text>
                <Spacer y={4} />
                {state.error && (
                    <>
                        <Advice type="error" showIcon defaultCollapsed={false} showCollapse={false}>
                            {state.error}
                        </Advice>
                        <Spacer y={2} />
                    </>
                )}
                <Input
                    label={t('label_login_email')}
                    value={formState.email}
                    isReadOnly
                    isFullWidth
                />
                <Input
                    label={t('label_full_name')}
                    placeholder={t('placeholder_name')}
                    isRequired
                    value={formState.fullName}
                    error={touchedRef.current.fullName && errors.fullName}
                    onChange={onChange('fullName')}
                    onBlur={() => onBlur('fullName')}
                    isFullWidth
                    role="presentation"
                    autocomplete="off"
                />
                <Spacer y={8} />
                {!urlParams.hideTermsAndPrivacy && (
                    <>
                        <CheckboxGroup
                            color="actionMajor"
                            error={errors.checks}
                            isFullWidth
                            onChange={onChange('checks')}
                            options={getChecksOptions}
                            value={formState.checks}
                        />
                        <Spacer y={2} />
                    </>
                )}
                <div className="sage-mobile-invite__actions">
                    <Button
                        type="primary"
                        size="big"
                        isLoading={state.loading}
                        isDisabled={(!validForm && isValidatingForm.current) || !btnActive}
                        className="sage-mobile-invite__submit-button"
                        onClick={handleSend}
                    >
                        {t('label_join_now')}
                    </Button>
                </div>
            </form>
        </div>
    );
});

SageMobileInvite.displayName = 'SageMobileInvite';

export default SageMobileInvite;
